<template>
  <div>
    <!-- Enhanced Filters Section -->
    <div class="filters-section">
      <div class="filters-header">
        <h4>
          <a-icon type="filter" class="filter-icon" />
          {{ $t("all.filtresEtRecherche") }}
        </h4>
        <div class="action-button-group">
          <a-button
            icon="file-pdf"
            type="primary"
            @click="generatePdf"
            :loading="printLoading"
            :disabled="printLoading"
            class="action-button"
          >
            {{ $t("action.imprimer") }}
          </a-button>
          <a-button icon="reload" @click="resetFilters" class="action-button">
            {{ $t("action.reinitialiser") }}
          </a-button>
        </div>
      </div>

      <a-row :gutter="[16, 16]" class="align-items-end">
        <!-- First Row: Search, Type, Bank Status -->
        <!-- Search Input -->
        <a-col :xs="24" :sm="8" :md="8" :lg="8">
          <div class="filter-item">
            <label class="filter-label">{{ $t("tresorerie.recherche") }}</label>
            <a-input
              v-model="searchQuery"
              :placeholder="$t('personnel.chercher')"
              @input="handleSearchInput"
              allow-clear
              class="search-input"
            >
              <a-icon slot="prefix" type="search" />
            </a-input>
          </div>
        </a-col>

        <!-- Type Filter -->
        <a-col :xs="24" :sm="8" :md="8" :lg="8">
          <div class="filter-item">
            <label class="filter-label"> {{ $t("tresorerie.type") }}</label>
            <a-select
              v-model="typeFilter"
              :placeholder="$t('recette.type')"
              @change="handleFilter"
              allow-clear
              class="filter-select"
            >
              <a-select-option value="CHEQUE">
                <a-icon type="credit-card" class="type-icon" />
                {{ $t("tresorerie.cheque") }}
              </a-select-option>
              <a-select-option value="TRAITE">
                <a-icon type="file-text" class="type-icon" />
                {{ $t("tresorerie.traiteType") }}
              </a-select-option>
              <a-select-option value="VIREMENT">
                <a-icon type="swap" class="type-icon" />
                {{ $t("recette.paymentMethod.VIREMENT") }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>

        <!-- Bank Status Filter -->
        <a-col :xs="24" :sm="8" :md="8" :lg="8">
          <div class="filter-item">
            <label class="filter-label">{{
              $t("tresorerie.statutBanque")
            }}</label>
            <a-select
              v-model="bankStatusFilter"
              :placeholder="$t('tresorerie.statutBanque')"
              @change="handleFilter"
              allow-clear
              class="filter-select"
            >
              <a-select-option value="VALIDATED">
                <a-icon type="check-circle" class="status-icon active" />
                {{ $t("status.VALIDATED") }}
              </a-select-option>
              <a-select-option value="PENDING">
                <a-icon type="clock-circle" class="status-icon pending" />
                {{ $t("status.PENDING") }}
              </a-select-option>
              <a-select-option value="REJECTED">
                <a-icon type="close-circle" class="status-icon inactive" />
                {{ $t("status.REJECTED") }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>
      </a-row>

      <a-row
        :gutter="[16, 16]"
        class="align-items-end"
        style="margin-top: 16px"
      >
        <!-- Second Row: General Status, Date Range -->
        <!-- General Status Filter -->
        <a-col :xs="24" :sm="12" :md="12" :lg="12">
          <div class="filter-item">
            <label class="filter-label">{{
              $t("tresorerie.statutGeneral")
            }}</label>
            <a-select
              v-model="statusFilter"
              :placeholder="$t('tresorerie.statutGeneral')"
              @change="handleFilter"
              allow-clear
              class="filter-select"
            >
              <a-select-option value="active">
                <a-icon type="check-circle" class="status-icon active" />
                {{ $t("status.ACTIVE") }}
              </a-select-option>
              <a-select-option value="REPLACED">
                <a-icon type="swap" class="status-icon pending" />
                {{ $t("status.REPLACED") }}
              </a-select-option>
              <a-select-option value="CANCELLED">
                <a-icon type="close-circle" class="status-icon inactive" />
                {{ $t("status.CANCELLED") }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>

        <!-- Date Range -->
        <a-col :xs="24" :sm="12" :md="12" :lg="12">
          <div class="filter-item">
            <label class="filter-label">{{ $t("tresorerie.periode") }}</label>
            <a-range-picker
              :placeholder="[$t('paiement.dateDebut'), $t('paiement.dateFin')]"
              format="DD/MM/YYYY"
              v-model="dateRangeData"
              :mode="mode"
              @change="handleDateRange"
              class="date-picker"
            />
          </div>
        </a-col>
      </a-row>
    </div>

    <!-- Enhanced Table -->
    <div class="table-container">
      <a-table
        :loading="tableLoading"
        :rowKey="'_id'"
        :columns="columns"
        :data-source="data"
        :pagination="pagination"
        :scroll="{ x: 'max-content' }"
        @change="tableChanged"
        size="middle"
        :row-class-name="getRowClassName"
      >
        <template slot="name" slot-scope="text">
          <div key="name">
            <template> {{ text }} </template>
          </div>
        </template>
        <template slot="createdAt" slot-scope="text">
          <span class="date-cell">
            {{ moment(new Date(text)).format("DD/MM/YYYY") }}
          </span>
        </template>
        <template slot="dateEchance" slot-scope="text">
          <span v-if="text" class="date-cell">
            {{ moment(new Date(text)).format("DD/MM/YYYY") }}
            <br />
            <small class="text-muted">{{
              moment(new Date(text)).fromNow()
            }}</small>
          </span>
          <span class="text-center" v-else>--</span>
        </template>
        <template slot="type" slot-scope="text, record">
          <a-tag :color="getPaymentMethodColor(text)" class="type-tag">
            {{ $t("recette.paymentMethod." + text) }}
          </a-tag>
          <a-tag
            :color="getChequeStatutColor('REPLACED')"
            class="status-tag"
            v-if="record.status == 'REPLACED'"
          >
            {{ $t("status.REPLACED") }}
          </a-tag>
          <a-tag
            :color="getChequeStatutColor('CANCELLED')"
            class="status-tag"
            v-if="record.status == 'CANCELLED'"
          >
            {{ $t("status.CANCELLED") }}
          </a-tag>
        </template>
        <template slot="bankStatus" slot-scope="text">
          <a-tag :color="getChequeStatutColor(text)" class="status-tag">
            {{ $t("status." + text) }}
          </a-tag>
        </template>
        <template slot="relatedInvoice" slot-scope="text, record">
          <span v-if="record.relatedInvoice" class="invoice-cell">
            <a-icon type="file-text" class="action-icon text-primary" />
            {{ record.relatedInvoice.serial || "--" }}
          </span>
          <span v-else>--</span>
        </template>
        <template slot="caisse" slot-scope="text, record">
          <span
            v-if="record.relatedInvoice && record.relatedInvoice.caisse"
            class="caisse-cell"
          >
            <a-icon type="bank" class="action-icon text-success" />
            {{ record.relatedInvoice.caisse.name }}
            <br />
            <small class="text-muted"
              >({{ record.relatedInvoice.caisse.reference }})</small
            >
          </span>
          <span v-else>--</span>
        </template>
        <template slot="bank" slot-scope="text">
          {{ text && text.includes("-") ? text.split("-")[0] : text || "--" }}
        </template>

        <template slot="doneBy" slot-scope="text, record">
          <span
            v-if="record.relatedInvoice && record.relatedInvoice.doneBy"
            class="user-cell"
          >
            <a-icon type="user" class="action-icon" style="color: #722ed1" />
            {{ record.relatedInvoice.doneBy.userName || "--" }}
          </span>
          <span v-else>--</span>
        </template>
        <template slot="montant" slot-scope="text">
          <span class="amount-cell">
            {{ formatAmount(text) }}
          </span>
        </template>
        <template slot="details" slot-scope="text, record">
          <div class="action-buttons">
            <a-button
              type="primary"
              @click="() => openEditModal(record)"
              size="small"
              :disabled="
                record.bankStatus == 'VALIDATED' ||
                record.status == 'REPLACED' ||
                record.status == 'CANCELLED'
              "
              style="border-radius: 6px; margin-right: 8px"
            >
              <a-icon type="edit" />
              {{ $t("tresorerie.modifier") }}
            </a-button>

            <a-button
              type="default"
              @click="() => replaceWithCash(record)"
              size="small"
              :disabled="
                record.bankStatus == 'VALIDATED' ||
                record.status == 'REPLACED' ||
                record.status == 'CANCELLED'
              "
              style="
                border-radius: 6px;
                background-color: #52c41a;
                border-color: #52c41a;
                color: white;
              "
              :loading="record.replacingWithCash"
            >
              <a-icon type="dollar" />
              {{ $t("tresorerie.espece") }}
            </a-button>
          </div>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import { generateChequeTraitePdf } from "../shared/pdfGenerator";
import {
  getChequeStatutColor,
  getPaymentMethodColor,
} from "@/utilities/settings";
import { formatCurrency } from "@/utilities/countrySpecific";

export default {
  name: "ChequesTab",
  props: {
    settings: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dateRangeData: ["", ""],
      mode: ["date", "date"],
      searchQuery: "",
      typeFilter: undefined,
      bankStatusFilter: undefined,
      statusFilter: undefined, // Added statusFilter
      sortField: "",
      sortOrder: "",
      tableLoading: true,
      printLoading: false,
      data: [],
      filtredTable: [],
      filtredTableCache: [],
      rowData: [],
      searchTimeout: null,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} ${this.$t(
            "tresorerie.sur"
          )} ${total} ${this.$t("tresorerie.elements")}`,
        pageSizeOptions: ["10", "20", "50", "100"],
        size: "default",
        position: "bottom",
        style: { marginTop: "16px" },
      },
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("recette.dateAjout"),
          dataIndex: "createdAt",
          key: "createdAt",
          scopedSlots: { customRender: "createdAt" },
          sorter: true,
          sortDirections: ["descend", "ascend"],
          width: 120,
        },
        {
          title: this.$t("recette.numero"),
          dataIndex: "num",
          key: "num",
          scopedSlots: { customRender: "num" },
          width: 100,
        },
        {
          title: this.$t("recette.type"),
          dataIndex: "type",
          key: "type",
          scopedSlots: { customRender: "type" },
          width: 100,
        },
        {
          title: this.$t("recette.stats.bankStatus"),
          dataIndex: "bankStatus",
          key: "bankStatus",
          scopedSlots: { customRender: "bankStatus" },
          width: 120,
        },
        {
          title: this.$t("recette.banque"),
          dataIndex: "bank",
          key: "bank",
          scopedSlots: { customRender: "bank" },
          width: 100,
        },
        {
          title: this.$t("recette.titulaire"),
          dataIndex: "titulaire",
          key: "titulaire",
          scopedSlots: { customRender: "titulaire" },
          width: 150,
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "montant",
          scopedSlots: { customRender: "montant" },
          sorter: true,
          sortDirections: ["descend", "ascend"],
          width: 120,
        },
        {
          title: this.$t("tresorerie.factureLabel"),
          dataIndex: "relatedInvoice",
          key: "relatedInvoice",
          scopedSlots: { customRender: "relatedInvoice" },
          width: 120,
        },
        {
          title: this.$t("tresorerie.caisse"),
          dataIndex: "caisse",
          key: "caisse",
          scopedSlots: { customRender: "caisse" },
          width: 150,
        },
        {
          title: this.$t("recette.dateEcheance"),
          dataIndex: "dateEchance",
          key: "dateEchance",
          scopedSlots: { customRender: "dateEchance" },
          sorter: true,
          sortDirections: ["descend", "ascend"],
          width: 140,
        },
        {
          title: this.$t("recette.doneBy"),
          dataIndex: "doneBy",
          key: "doneBy",
          scopedSlots: { customRender: "doneBy" },
          width: 120,
        },
        {
          title: this.$t("action.actions"),
          dataIndex: "details",
          key: "details",
          fixed: "right",
          scopedSlots: {
            customRender: "details",
          },
          width: 180,
        },
      ];
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    getChequeStatutColor,
    getPaymentMethodColor,
    async fetchData(page = 1, pageSize = 10) {
      try {
        this.tableLoading = true;

        const params = {
          page,
          limit: pageSize,
        };

        // Add search query
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        // Add type filter
        if (this.typeFilter) {
          params.type = this.typeFilter;
        }

        // Add bank status filter
        if (this.bankStatusFilter) {
          params.bankStatus = this.bankStatusFilter;
        }

        // Add general status filter
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }

        // Add sorting parameters
        if (this.sortField && this.sortOrder) {
          params.sortField = this.sortField;
          params.sortOrder = this.sortOrder;
        }

        const response = await apiClient.get("/compV3/cheque-traite", {
          params,
        });

        this.data = response.data.data;
        this.rowData = this.data.map((item) => ({ ...item }));
        this.filtredTable = this.data.map((item) => ({ ...item }));
        this.filtredTableCache = this.data.map((item) => ({ ...item }));

        // Update pagination
        this.pagination.current = response.data.pagination.currentPage;
        this.pagination.total = response.data.pagination.totalItems;
        this.pagination.pageSize = response.data.pagination.itemsPerPage;

        this.tableLoading = false;
      } catch (error) {
        console.error("Error fetching data:", error);
        this.tableLoading = false;
        this.$message.error(this.$t("tresorerie.erreurChargementDonnees"));
      }
    },

    handleSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(() => {
        this.pagination.current = 1;
        this.fetchData();
      }, 500); // 500ms delay
    },

    handleFilter() {
      this.pagination.current = 1;
      this.fetchData();
    },

    resetFilters() {
      // Clear search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchQuery = "";
      this.typeFilter = undefined;
      this.bankStatusFilter = undefined;
      this.statusFilter = undefined; // Reset statusFilter
      this.dateRangeData = ["", ""];
      this.sortField = "";
      this.sortOrder = "";
      this.pagination.current = 1;
      this.fetchData();
    },

    handleDateRange(value) {
      if (value && value.length == 2) {
        const start = moment(value[0]);
        const end = moment(value[1]);

        this.data = this.rowData.filter((elem) => {
          return moment(elem.dateEchance).isBetween(start, end, "days", "[]");
        });

        this.filtredTable = this.filtredTableCache.filter((elem) => {
          return moment(elem.dateEchance).isBetween(start, end, "days", "[]");
        });
      } else {
        this.data = this.rowData;
        this.filtredTable = this.filtredTableCache;
      }
    },

    formatAmount(amount) {
      return formatCurrency(amount);
    },

    getRowClassName(record, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },

    openEditModal(record) {
      this.$emit("openEditModal", record);
    },

    async replaceWithCash(record) {
      try {
        // Show confirmation modal
        this.$confirm({
          title: this.$t("tresorerie.remplacerParEspece"),
          content: this.$t("tresorerie.confirmationRemplacement", {
            numero: record.num,
            montant: this.formatAmount(record.montant),
          }),
          okText: this.$t("action.confirmer"),
          cancelText: this.$t("all.cancel"),
          okType: "primary",
          onOk: async () => {
            // Set loading state for this specific record
            this.$set(record, "replacingWithCash", true);

            try {
              const response = await apiClient.patch(
                `/compV3/cheque-traite/${record._id}/replace-with-cash`,
                {
                  note: this.$t("tresorerie.remplacementChequeNote", {
                    numero: record.num,
                  }),
                }
              );

              if (response.data.success) {
                this.$message.success(
                  this.$t("tresorerie.chequeRemplaceSucces")
                );

                // Update the record status in the table
                record.status = "REPLACED";
                record.replacedAt = new Date();

                // Optionally refresh the data
                await this.fetchData(
                  this.pagination.current,
                  this.pagination.pageSize
                );
              } else {
                this.$message.error(
                  this.$t("tresorerie.erreurRemplacementCheque")
                );
              }
            } catch (error) {
              console.error("Error replacing cheque with cash:", error);

              // Handle different error scenarios
              if (error.response?.data?.message) {
                this.$message.error(error.response.data.message);
              } else if (error.response?.status === 400) {
                this.$message.error(this.$t("tresorerie.chequeNonEligible"));
              } else if (error.response?.status === 404) {
                this.$message.error(this.$t("tresorerie.chequeNonTrouve"));
              } else {
                this.$message.error(
                  this.$t("tresorerie.erreurRemplacementCheque")
                );
              }
            } finally {
              // Remove loading state
              this.$set(record, "replacingWithCash", false);
            }
          },
        });
      } catch (error) {
        console.error("Error in replaceWithCash:", error);
      }
    },

    tableChanged(pagination, filters, sorter, extra) {
      // Handle sorting
      if (sorter && sorter.field) {
        this.sortField = sorter.field;
        this.sortOrder = sorter.order === "ascend" ? "asc" : "desc";
        this.pagination.current = 1; // Reset to first page when sorting
        this.fetchData();
        return;
      }

      // Handle pagination change
      if (
        pagination.current !== this.pagination.current ||
        pagination.pageSize !== this.pagination.pageSize
      ) {
        this.fetchData(pagination.current, pagination.pageSize);
      }
    },

    generatePdf() {
      // Track PDF generation event
      this.$gtag.event("Imp Les chèques", {
        event_category: "Impression PDF",
        event_label: "comptabilite:chèques",
        value: 1,
      });

      this.printLoading = true;

      try {
        // Prepare filters object
        const filters = {
          type: this.typeFilter,
          bankStatus: this.bankStatusFilter,
          status: this.statusFilter,
          search: this.searchQuery,
        };

        // Generate PDF using the refactored utility
        const result = generateChequeTraitePdf({
          data: this.filtredTable,
          dateRange: this.dateRangeData,
          settings: this.settings,
          filters: filters,
          t: this.$t.bind(this),
          locale: this.$i18n.locale,
        });

        if (result.success) {
          this.$message.success(result.message);
        } else {
          this.$message.error(result.message);
        }
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.$message.error(this.$t("tresorerie.erreurChargementDonnees"));
      } finally {
        this.printLoading = false;
      }
    },

    moment,
  },
};
</script>

<style>
@import "../../../common.css";

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.action-buttons .ant-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-buttons .ant-btn:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.action-buttons .ant-btn[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Ensure action column has enough space */
.ant-table-fixed-right {
  min-width: 180px;
}
</style>
